import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adm, apiErr } from "../../lib/adminApi";
import { PageHead, TInput, TArea, Btn, Card, MediaPicker, Badge, Toggle, ApprovalSel } from "../../components/admin/ui";

function DimensionEditor({ d, onSaved }) {
  const [x, setX] = useState(d);
  const set = (k, v) => setX((o) => ({ ...o, [k]: v }));
  const setCa = (f, v) => setX((o) => ({ ...o, copyApproval: { ...(o.copyApproval || {}), [f]: v } }));
  const setFt = (k, v) => setX((o) => ({ ...o, featuredTrack: { ...(o.featuredTrack || {}), [k]: v } }));
  const setCta = (key, f, v) => setX((o) => ({ ...o, [key]: { ...(o[key] || {}), [f]: v } }));
  const save = async () => {
    try {
      const { _id, number, source, createdAt, updatedAt, restricted, ...body } = x;
      await adm.update("dimensions", d.number, body);
      toast.success(`Dimension ${d.number} saved.`);
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="mb-4" >
      <div className="flex items-center justify-between mb-4">
        <p className="font-serif text-xl text-white">{d.number} — {x.title} {d.restricted && <Badge tone="red">RESTRICTED</Badge>}</p>
        <Btn onClick={save} testid={`dimension-save-${d.number}`}>SAVE</Btn>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <TInput label="TITLE" value={x.title} onChange={(v) => set("title", v)} />
        <TInput label="THEMATIC DESIGNATION" value={x.theme} onChange={(v) => set("theme", v)} />
        <TInput label="STATUS" value={x.status} onChange={(v) => set("status", v)} testid={`dimension-status-${d.number}`} />
        <MediaPicker label="APPROVED IMAGERY (URL)" value={x.artwork} onChange={(v) => set("artwork", v || null)} mode="url" filterType="image" />
        <TInput label="ASSOCIATED RELEASE SLUG" value={x.releaseId} onChange={(v) => set("releaseId", v || null)} />
        <div className="grid grid-cols-2 gap-3">
          <TInput label="CTA LABEL" value={x.cta?.label} onChange={(v) => setCta("cta", "label", v)} />
          <TInput label="CTA TARGET" value={x.cta?.target} onChange={(v) => setCta("cta", "target", v)} />
        </div>
        <div className="md:col-span-3">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[9px] tracking-[0.25em] text-white/45">DESCRIPTION</span>
            <ApprovalSel value={x.copyApproval?.description} onChange={(v) => setCa("description", v)} testid={`dimension-${d.number}-approval-description`} />
          </div>
          <TArea label="" value={x.description} onChange={(v) => set("description", v)} rows={2} />
        </div>
      </div>

      <p className="font-mono text-[9px] tracking-[0.3em] text-gold/80 mt-6 mb-3">HOMEPAGE HERO CAROUSEL</p>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="flex items-end pb-1"><Toggle label="SHOW IN HERO" checked={x.showInHero !== false} onChange={(v) => set("showInHero", v)} testid={`dimension-${d.number}-showhero`} /></div>
        <TInput label="HERO ORDER" value={x.heroOrder} onChange={(v) => set("heroOrder", v === "" ? null : Number(v))} type="number" />
        <TInput label="HERO TAGLINE (OPTIONAL — DEFAULTS TO THEME)" value={x.heroTagline} onChange={(v) => set("heroTagline", v || null)} testid={`dimension-${d.number}-tagline`} />
        <div className="md:col-span-3">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[9px] tracking-[0.25em] text-white/45">HERO DESCRIPTION (OPTIONAL — DEFAULTS TO DESCRIPTION)</span>
            <ApprovalSel value={x.copyApproval?.heroDescription} onChange={(v) => setCa("heroDescription", v)} testid={`dimension-${d.number}-approval-hero`} />
          </div>
          <TArea label="" value={x.heroDescription} onChange={(v) => set("heroDescription", v || null)} rows={2} />
        </div>
        {!d.restricted && (
          <>
            <TInput label="FEATURED TRACK — RELEASE SLUG" value={x.featuredTrack?.releaseSlug} onChange={(v) => setFt("releaseSlug", v || null)} testid={`dimension-${d.number}-ft-slug`} />
            <TInput label="FEATURED TRACK — TRACK NUMBER" value={x.featuredTrack?.trackNumber} onChange={(v) => setFt("trackNumber", v ? Number(v) : null)} type="number" testid={`dimension-${d.number}-ft-number`} />
            <div />
            <div className="grid grid-cols-2 gap-3">
              <TInput label="HERO CTA 1 LABEL" value={x.heroCtaPrimary?.label} onChange={(v) => setCta("heroCtaPrimary", "label", v)} />
              <TInput label="HERO CTA 1 TARGET" value={x.heroCtaPrimary?.target} onChange={(v) => setCta("heroCtaPrimary", "target", v)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <TInput label="HERO CTA 2 LABEL" value={x.heroCtaSecondary?.label} onChange={(v) => setCta("heroCtaSecondary", "label", v)} />
              <TInput label="HERO CTA 2 TARGET" value={x.heroCtaSecondary?.target} onChange={(v) => setCta("heroCtaSecondary", "target", v)} />
            </div>
          </>
        )}
      </div>
      <p className="font-mono text-[8px] text-white/30 mt-4">
        WHISPER STATE: {x.whisper?.enabled ? "ENABLED" : "DISABLED"} — official phrases are managed in the Whispers console.
        Hero CTAs default to ENTER DIMENSION / EXPLORE ALBUM when left blank.
      </p>
      {!d.restricted && (
        <>
          <p className="font-mono text-[9px] tracking-[0.3em] text-gold/80 mt-6 mb-3">SOCIAL PREVIEW CARD (SHARE LINK: /api/share/dimension/{d.number})</p>
          <div className="grid md:grid-cols-3 gap-4">
            <TInput label="SOCIAL TITLE" value={x.social?.title} onChange={(v) => setX((o) => ({ ...o, social: { ...(o.social || {}), title: v || null } }))} testid={`dimension-${d.number}-social-title`} />
            <TInput label="SOCIAL DESCRIPTION" value={x.social?.description} onChange={(v) => setX((o) => ({ ...o, social: { ...(o.social || {}), description: v || null } }))} />
            <MediaPicker label="SOCIAL ARTWORK" value={x.social?.image} onChange={(v) => setX((o) => ({ ...o, social: { ...(o.social || {}), image: v || null } }))} mode="url" filterType="image" />
          </div>
        </>
      )}
    </Card>
  );
}

export default function AdminDimensions() {
  const [items, setItems] = useState([]);
  const refresh = () => adm.list("dimensions").then((d) => setItems(d.sort((a, b) => a.number.localeCompare(b.number)))).catch(() => {});
  useEffect(() => { refresh(); }, []);
  return (
    <div data-testid="admin-dimensions">
      <PageHead title="Dimensions" sub="Public content + hero carousel configuration for each Dimension. Do not invent artwork or lore for unreleased Dimensions." />
      {items.map((d) => <DimensionEditor key={d.number} d={d} onSaved={refresh} />)}
    </div>
  );
}
